import {removeCookie, setCookie} from '@vio/core/src/_web/utils/cookies'
import {isBrowser} from '@vio/core/src/_web/utils/environment'
import {
  safelyGetStorageItem,
  safelySetStorageItem
} from '@vio/core/src/_web/utils/persistence'
import {getDomainName} from '@vio/core/src/utils/url'

export const OTA_MODE_STICKY_COOKIE = 'otaMode'
export const OTA_MODE_STICKY_STORAGE_KEY = 'otaMode'
const STICKY_VALUE = '1'

interface ServerCookiesReq {
  cookies?: Record<string, string>
}

const isStickyValue = (value: string | null | undefined): boolean =>
  value === STICKY_VALUE

/**
 * Returns a `{domain}` option only when the current hostname has more than one
 * label (e.g. `vio.com`, `www.vio.com`). Browsers reject explicit Domain attributes
 * on single-label hostnames like `localhost`, which would silently drop the
 * cookie in dev. When omitted, the browser scopes the cookie to the current host.
 */
const getDomainAttribute = (): {domain?: string} => {
  const {hostname} = window.location
  if (!hostname.includes('.')) return {}
  return {domain: getDomainName(hostname)}
}

/**
 * Reads the sticky OTA Mode flag.
 *
 * - On the **server** (`req` passed): reads the request cookie. This is the only
 *   way SSR can know about the sticky flag.
 * - On the **client** (no `req`): reads `sessionStorage` only — it is the
 *   client-side source of truth. We intentionally do NOT fall back to the
 *   cookie: closing the tab clears sessionStorage and ends the sticky session,
 *   even when the browser-session cookie is still present (e.g. cookie was
 *   inherited by a freshly-opened tab from the same browser session).
 */
export const hasStickyOtaMode = (req?: ServerCookiesReq): boolean => {
  if (req) {
    return isStickyValue(req.cookies?.[OTA_MODE_STICKY_COOKIE])
  }
  if (!isBrowser) return false

  return isStickyValue(
    safelyGetStorageItem(
      'sessionStorage',
      OTA_MODE_STICKY_STORAGE_KEY,
      'hasStickyOtaMode: sessionStorage read failed'
    )
  )
}

/**
 * Writes the sticky flag to both sessionStorage (tab-scoped client SOT) and a
 * browser-session cookie (SSR-readable, no `Max-Age` so it dies with the
 * browser session). `SameSite=Lax` lets the cookie ride top-level GET
 * re-entries from partner sites.
 */
export const setStickyOtaMode = (): void => {
  if (!isBrowser) return

  safelySetStorageItem(
    'sessionStorage',
    OTA_MODE_STICKY_STORAGE_KEY,
    STICKY_VALUE,
    'setStickyOtaMode: sessionStorage write failed'
  )

  setCookie(OTA_MODE_STICKY_COOKIE, STICKY_VALUE, {
    path: '/',
    ...getDomainAttribute(),
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production'
  })
}

/**
 * Clears both stores. Used by the bootstrap's reconciliation path when a new
 * tab inherits a stale cookie from the same browser session: sessionStorage is
 * empty (tab is fresh) but the cookie is present (browser-shared), so we drop
 * the cookie to bring the rest of the page (and any future SSR requests) back
 * to a non-sticky state.
 */
export const clearStickyOtaMode = (): void => {
  if (!isBrowser) return

  try {
    window.sessionStorage.removeItem(OTA_MODE_STICKY_STORAGE_KEY)
  } catch {
    // ignore — storage may be disabled
  }

  removeCookie(OTA_MODE_STICKY_COOKIE, {
    path: '/',
    ...getDomainAttribute()
  })
}
